import { StopTypeFilter } from '@numeo/types-core'
import { DEFAULT_TRIP_DATE_BASIS, type TripDateBasis } from '@numeo/types-main-web-app'

/** The filters the Ongoing Loads list sends, as they travel in the query string. */
export interface OngoingLoadsListParams {
    status: string[]
    searchTerm?: string
    dispatcherIds?: string[] | string
    loadGroupId?: string
    sort?: string
    brokers?: string[]
    brokerFilterMode?: 'filter' | 'priority'
    laneFilterMode?: 'all' | 'only' | 'exclude'
    attentionNeeded?: boolean
    unassignedRiskThresholds?: number[]
    startDate?: string
    endDate?: string
    /** Which day of the trip the range matches; sent only with a range, and only when it is not the pickup day. */
    dateBasis?: TripDateBasis
    driverIds?: string[]
    truck?: string
    truckIds?: string[]
    stopTypeFilter?: (StopTypeFilter | 'all')[]
    tagIds?: string[]
    payrollBatchIds?: string[]
    /** Ask for each row's Batch/Period; the list sends it while the column is on screen, the export does not. */
    includePayrollPlacement?: boolean
    /** The Excel export's Delivered-tab Finance chip; the list does not send it. */
    financeStatus?: string
}

/**
 * The one encoder of those filters: `ongoingLoadsApi.listLoads` (the list) adds its paging to the result and the Excel trips export sends it as is,
 * so the export asks for exactly the rows the list asks for. Defaults travel as "not sent" (`all` lane mode, `all` stop types / drivers, empty lists).
 */
export const encodeOngoingLoadsListParams = (p: OngoingLoadsListParams): URLSearchParams => {
    const params = new URLSearchParams()
    const statusQuery = p.status.join(',')
    if (statusQuery) params.append('status', statusQuery)
    if (p.searchTerm) params.append('searchTerm', p.searchTerm)
    if (p.brokers && p.brokers.length) params.append('brokers', JSON.stringify(p.brokers))
    if (p.brokerFilterMode) params.append('brokerFilterMode', p.brokerFilterMode)
    if (p.laneFilterMode && p.laneFilterMode !== 'all') params.append('laneFilterMode', p.laneFilterMode)
    if (typeof p.dispatcherIds === 'string') {
        if (p.dispatcherIds) params.append('dispatcherIds', p.dispatcherIds)
    } else if (p.dispatcherIds && p.dispatcherIds.length) {
        params.append('dispatcherIds', p.dispatcherIds.join(','))
    }
    if (p.loadGroupId) params.append('loadGroupId', p.loadGroupId)
    if (p.sort) params.append('sort', p.sort)
    if (p.attentionNeeded) params.append('attentionNeeded', 'true')
    if (p.unassignedRiskThresholds && p.unassignedRiskThresholds.length) params.append('unassignedRiskThresholds', p.unassignedRiskThresholds.join(','))
    if (p.startDate) params.append('startDate', p.startDate)
    if (p.endDate) params.append('endDate', p.endDate)
    if ((p.startDate || p.endDate) && p.dateBasis && p.dateBasis !== DEFAULT_TRIP_DATE_BASIS) params.append('dateBasis', p.dateBasis)
    if (p.driverIds && p.driverIds.length && !p.driverIds.includes('all')) params.append('driverIds', p.driverIds.join(','))
    if (p.truck) params.append('truck', p.truck)
    if (p.truckIds && p.truckIds.length) params.append('truckIds', p.truckIds.join(','))
    if (p.stopTypeFilter && p.stopTypeFilter.length && !p.stopTypeFilter.includes(StopTypeFilter.ALL))
        params.append('stopTypeFilter', p.stopTypeFilter.join(','))
    if (p.tagIds && p.tagIds.length) params.append('tagIds', p.tagIds.join(','))
    if (p.payrollBatchIds && p.payrollBatchIds.length) params.append('payrollBatchIds', p.payrollBatchIds.join(','))
    if (p.includePayrollPlacement) params.append('includePayrollPlacement', 'true')
    if (p.financeStatus) params.append('financeStatus', p.financeStatus)
    return params
}
